// components/GlobalCandleChart/GlobalCandleChart.jsx
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
  CrosshairMode,
  createSeriesMarkers,
} from "lightweight-charts";

/* ═══ TIME: parse "DD/MM/YYYY, HH:MM" as literal wall-clock → unix sec ═══ */
const toUnix = (str) => {
  if (!str) return null;
  const [d, t = "00:00"] = String(str)
    .split(",")
    .map((s) => s.trim());
  const [dd, mm, yyyy] = d.split("/").map(Number);
  const [hh, min] = t.split(":").map(Number);
  if (!yyyy || !mm || !dd) return null;
  return Math.floor(Date.UTC(yyyy, mm - 1, dd, hh, min) / 1000);
};

const formatTimeZone = (sec, timeZone) =>
  new Date(Number(sec) * 1000).toLocaleTimeString("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const formatDate = (sec) =>
  new Date(Number(sec) * 1000).toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
  });

const fmtTime = (sec) => formatTimeZone(sec, "America/New_York");

const fmtFull = (sec) =>
  `${formatDate(sec)}, ${formatTimeZone(sec, "America/New_York")}`;
// format with UTC so the number in == the number out (no zone drift)
// const fmtTime = (sec) =>
//   new Date(sec * 1000).toLocaleTimeString("en-US", {
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: true,
//     timeZone: "UTC",
//   });
// const fmtFull = (sec) =>
//   new Date(sec * 1000).toLocaleString("en-US", {
//     month: "short",
//     day: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: true,
//     timeZone: "UTC",
//   });

/* ═══ INDICATOR HELPERS ═══ */
const ema = (src, p) => {
  const k = 2 / (p + 1);
  const out = [src[0]];
  for (let i = 1; i < src.length; i++)
    out.push(src[i] * k + out[i - 1] * (1 - k));
  return out;
};
const rma = (src, p) => {
  const out = [src[0]];
  for (let i = 1; i < src.length; i++)
    out.push((out[i - 1] * (p - 1) + src[i]) / p);
  return out;
};
const atr = (candles, p) => {
  const tr = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high,
      l = candles[i].low,
      pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  return rma(tr, p);
};

const aggregate = (data, step) => {
  if (step <= 1) return data;
  const out = [];
  for (let i = 0; i < data.length; i += step) {
    const chunk = data.slice(i, i + step);
    if (!chunk.length) continue;
    out.push({
      time: chunk[0].time,
      open: chunk[0].open,
      high: Math.max(...chunk.map((c) => c.high)),
      low: Math.min(...chunk.map((c) => c.low)),
      close: chunk[chunk.length - 1].close,
      volume: chunk.reduce((s, c) => s + (c.volume || 0), 0),
    });
  }
  return out;
};

const calcVWAP = (candles, source = "hlc3") => {
  let cpv = 0,
    cv = 0;
  return candles.map((c) => {
    let p;
    if (source === "close") p = c.close;
    else if (source === "high") p = c.high;
    else if (source === "low") p = c.low;
    else p = (c.high + c.low + c.close) / 3;
    const v = c.volume || 0;
    cpv += p * v;
    cv += v;
    return { time: c.time, value: cv > 0 ? cpv / cv : null };
  });
};

const calcBB = (candles, period = 20, mult = 2) => {
  if (candles.length < period) return { upper: [], middle: [], lower: [] };
  const upper = [],
    middle = [],
    lower = [];
  for (let i = period - 1; i < candles.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += candles[j].close;
    const sma = sum / period;
    let sq = 0;
    for (let j = i - period + 1; j <= i; j++)
      sq += (candles[j].close - sma) ** 2;
    const sd = Math.sqrt(sq / period);
    upper.push({ time: candles[i].time, value: sma + mult * sd });
    middle.push({ time: candles[i].time, value: sma });
    lower.push({ time: candles[i].time, value: sma - mult * sd });
  }
  return { upper, middle, lower };
};

const calcUTBot = (candles, key = 3, atrPeriod = 10) => {
  if (candles.length < 2) return { line: [], signals: [] };
  const atrVals = atr(candles, atrPeriod);
  const line = [],
    signals = [];
  let prevStop = candles[0].close,
    direction = 0;
  for (let i = 0; i < candles.length; i++) {
    const src = candles[i].close;
    const prevSrc = i > 0 ? candles[i - 1].close : src;
    const nLoss = key * atrVals[i];
    let stop;
    if (i === 0) stop = src;
    else if (src > prevStop && prevSrc > prevStop)
      stop = Math.max(prevStop, src - nLoss);
    else if (src < prevStop && prevSrc < prevStop)
      stop = Math.min(prevStop, src + nLoss);
    else stop = src > prevStop ? src - nLoss : src + nLoss;
    if (i > 0) {
      const crossUp = prevSrc <= prevStop && src > stop;
      const crossDown = prevSrc >= prevStop && src < stop;
      if (crossUp && direction !== 1) {
        signals.push({
          time: candles[i].time,
          position: "belowBar",
          color: "#22c55e",
          shape: "arrowUp",
          text: "",
          size: 1.5,
        });
        direction = 1;
      }
      if (crossDown && direction !== -1) {
        signals.push({
          time: candles[i].time,
          position: "aboveBar",
          color: "#ef4444",
          shape: "arrowDown",
          text: "",
          size: 1.5,
        });
        direction = -1;
      }
    }
    line.push({ time: candles[i].time, value: stop });
    prevStop = stop;
  }
  return { line, signals };
};

const calcVWAPSignals = (candles, vwapData) => {
  const vMap = new Map();
  vwapData.forEach((v) => vMap.set(v.time, v.value));
  const signals = [];
  let dir = 0;
  for (let i = 1; i < candles.length; i++) {
    const pv = vMap.get(candles[i - 1].time);
    const cv = vMap.get(candles[i].time);
    if (pv == null || cv == null) continue;
    if (candles[i - 1].close < pv && candles[i].close > cv && dir !== 1) {
      signals.push({
        time: candles[i].time,
        position: "belowBar",
        color: "#22c55e",
        shape: "arrowUp",
        text: "",
        size: 1.5,
      });
      dir = 1;
    }
    if (candles[i - 1].close > pv && candles[i].close < cv && dir !== -1) {
      signals.push({
        time: candles[i].time,
        position: "aboveBar",
        color: "#ef4444",
        shape: "arrowDown",
        text: "",
        size: 1.5,
      });
      dir = -1;
    }
  }
  return signals;
};

const calcBBSignals = (candles, bb) => {
  if (!bb.lower.length) return [];
  const map = new Map();
  bb.lower.forEach((d, i) =>
    map.set(d.time, { lo: d.value, hi: bb.upper[i]?.value }),
  );
  const signals = [];
  let dir = 0;
  for (let i = 2; i < candles.length; i++) {
    const curr = map.get(candles[i].time);
    const prev = map.get(candles[i - 1].time);
    if (!curr || !prev) continue;
    if (
      candles[i - 1].close <= prev.lo &&
      candles[i].close > curr.lo &&
      candles[i].close > candles[i].open &&
      dir !== 1
    ) {
      signals.push({
        time: candles[i].time,
        position: "belowBar",
        color: "#22c55e",
        shape: "arrowUp",
        text: "",
        size: 1.5,
      });
      dir = 1;
    }
    if (
      candles[i - 1].close >= prev.hi &&
      candles[i].close < curr.hi &&
      candles[i].close < candles[i].open &&
      dir !== -1
    ) {
      signals.push({
        time: candles[i].time,
        position: "aboveBar",
        color: "#ef4444",
        shape: "arrowDown",
        text: "",
        size: 1.5,
      });
      dir = -1;
    }
  }
  return signals;
};

const calcMACD = (candles, fast = 12, slow = 26, sig = 9) => {
  if (candles.length < slow + sig)
    return { macdLine: [], signalLine: [], histogram: [], crossovers: [] };
  const closes = candles.map((c) => c.close);
  const emaF = ema(closes, fast),
    emaS = ema(closes, slow);
  const macdRaw = emaF.map((v, i) => v - emaS[i]);
  const macdSlice = macdRaw.slice(slow - 1);
  const sigLine = ema(macdSlice, sig);
  const macdLine = [],
    signalLine = [],
    histogram = [],
    crossovers = [];
  const offset = slow - 1;
  let prevDir = 0;
  for (let i = 0; i < macdSlice.length; i++) {
    if (i < sig - 1) continue;
    const ci = offset + i;
    const m = macdSlice[i],
      s = sigLine[i],
      h = m - s;
    macdLine.push({ time: candles[ci].time, value: m });
    signalLine.push({ time: candles[ci].time, value: s });
    histogram.push({
      time: candles[ci].time,
      value: h,
      color: h >= 0 ? "#26a69a" : "#ef5350",
    });
    if (i >= sig) {
      const pm = macdSlice[i - 1],
        ps = sigLine[i - 1];
      if (pm <= ps && m > s && prevDir !== 1) {
        crossovers.push({
          time: candles[ci].time,
          position: "belowBar",
          color: "#22c55e",
          shape: "arrowUp",
          text: "",
          size: 1,
        });
        prevDir = 1;
      }
      if (pm >= ps && m < s && prevDir !== -1) {
        crossovers.push({
          time: candles[ci].time,
          position: "aboveBar",
          color: "#ef4444",
          shape: "arrowDown",
          text: "",
          size: 1,
        });
        prevDir = -1;
      }
    }
  }
  return { macdLine, signalLine, histogram, crossovers };
};

/* ═══ TOOLTIP ═══ */
const makeTooltip = (container) => {
  const el = document.createElement("div");
  el.style.cssText = `position:absolute;top:8px;left:12px;z-index:10;pointer-events:none;
    font:600 12px/1.6 monospace;color:#cbd5e1;background:rgba(15,23,42,0.88);
    padding:4px 10px;border-radius:6px;white-space:nowrap;
    font-variant-numeric:tabular-nums;display:none;`;
  container.style.position = "relative";
  container.appendChild(el);
  return el;
};

/* ═══ COMPONENT ═══ */
export default function GlobalCandleChart({ data, isMaximized = false }) {
  const containerRef = useRef(null);
  const priceRef = useRef(null);
  const chart = useRef(null);
  const candleSeries = useRef(null);
  const overlay = useRef(null);
  const bbUp = useRef(null);
  const bbMid = useRef(null);
  const bbLo = useRef(null);
  const mHist = useRef(null);
  const mLine = useRef(null);
  const mSig = useRef(null);
  const tip = useRef(null);

  const [indicator, setIndicator] = useState("vwap");
  const [tf, setTf] = useState("1m");
  const [vwapSrc, setVwapSrc] = useState("hlc3");
  const [bbP, setBbP] = useState(20);
  const [bbM, setBbM] = useState(2);

  // raw candles from prop
  //   const baseCandles = useMemo(() => {
  //     return (data || [])
  //       .map((r) => ({
  //         time: toUnix(r.date),
  //         open: +r.open,
  //         high: +r.high,
  //         low: +r.low,
  //         close: +r.close,
  //         volume: +r.volume,
  //       }))
  //       .filter((c) => c.time != null)
  //       .sort((a, b) => a.time - b.time);
  //   }, [data]);
  const baseCandles = useMemo(() => {
    const rows = data || [];
    if (!rows.length) return [];

    // group by calendar day (the DD/MM/YYYY part)
    const latestDay = rows
      .map((r) => r.date?.split(",")[0]?.trim())
      .filter(Boolean)
      .sort() // lexical won't work for DD/MM — see note
      .at(-1);

    return rows
      .filter((r) => r.date?.split(",")[0]?.trim() === latestDay)
      .map((r) => ({
        time: toUnix(r.date),
        open: +r.open,
        high: +r.high,
        low: +r.low,
        close: +r.close,
        volume: +r.volume,
      }))
      .filter((c) => c.time != null)
      .sort((a, b) => a.time - b.time);
  }, [data]);

  /* create chart once */
  useEffect(() => {
    if (!priceRef.current) return;
    const w = containerRef.current?.clientWidth || 800;
    const h = isMaximized ? window.innerHeight - 150 : 500;

    const pc = createChart(priceRef.current, {
      width: w,
      height: h,
      layout: { background: { color: "#0f172a" }, textColor: "#94a3b8" },
      grid: {
        vertLines: { color: "#1e293b" },
        horzLines: { color: "#1e293b" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: "#334155" },
      timeScale: {
        borderColor: "#334155",
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: fmtTime,
      },
      localization: { timeFormatter: fmtFull },
    });
    chart.current = pc;

    candleSeries.current = pc.addSeries(CandlestickSeries, {
      upColor: "#22c55e",
      downColor: "#ef4444",
      borderUpColor: "#22c55e",
      borderDownColor: "#ef4444",
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
    });
    overlay.current = pc.addSeries(LineSeries, {
      color: "#f59e0b",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    bbUp.current = pc.addSeries(LineSeries, {
      color: "#60a5fa",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    bbMid.current = pc.addSeries(LineSeries, {
      color: "#f97316",
      lineWidth: 1,
      lineStyle: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    bbLo.current = pc.addSeries(LineSeries, {
      color: "#60a5fa",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    mHist.current = pc.addSeries(HistogramSeries, {
      priceScaleId: "macd",
      priceLineVisible: false,
      lastValueVisible: false,
    });
    mLine.current = pc.addSeries(LineSeries, {
      color: "#3b82f6",
      lineWidth: 2,
      priceScaleId: "macd",
      priceLineVisible: false,
      lastValueVisible: false,
    });
    mSig.current = pc.addSeries(LineSeries, {
      color: "#f59e0b",
      lineWidth: 1,
      priceScaleId: "macd",
      priceLineVisible: false,
      lastValueVisible: false,
    });

    pc.priceScale("macd").applyOptions({
      scaleMargins: { top: 0.7, bottom: 0.02 },
      borderColor: "#334155",
      drawTicks: false,
    });
    pc.priceScale("right").applyOptions({
      scaleMargins: { top: 0.02, bottom: 0.32 },
    });

    tip.current = makeTooltip(priceRef.current);
    pc.subscribeCrosshairMove((param) => {
      const el = tip.current;
      if (!param.time || !param.seriesData) {
        el.style.display = "none";
        return;
      }
      const d = param.seriesData.get(candleSeries.current);
      if (!d || d.open == null) {
        el.style.display = "none";
        return;
      }
      const chg = d.close - d.open,
        pct = ((chg / d.open) * 100).toFixed(2);
      const col = chg >= 0 ? "#22c55e" : "#ef4444";
      el.style.display = "block";
      //   el.innerHTML = [
      //     `<span style="color:#64748b">${fmtFull(param.time)}</span>`,
      //     `O <b>${d.open.toFixed(2)}</b>`,
      //     `H <b style="color:#22c55e">${d.high.toFixed(2)}</b>`,
      //     `L <b style="color:#ef4444">${d.low.toFixed(2)}</b>`,
      //     `C <b style="color:${col}">${d.close.toFixed(2)}</b>`,
      //     `<span style="color:${col}">${chg >= 0 ? "+" : ""}${chg.toFixed(2)} (${pct}%)</span>`,
      //   ].join("&ensp;");
      const newYorkTime = formatTimeZone(param.time, "America/New_York");

      const utcTime = formatTimeZone(param.time, "UTC");

      const indiaTime = formatTimeZone(param.time, "Asia/Kolkata");

      const date = formatDate(param.time);

      el.innerHTML = `
  <div style="
    display:flex;
    align-items:center;
    gap:12px;
    flex-wrap:wrap;
  ">

    <span style="color:#64748b">
      ${date}
    </span>

    <span>
      NY <b>${newYorkTime}</b>
    </span>

    <span>
      UTC <b>${utcTime}</b>
    </span>

    <span>
      IST <b>${indiaTime}</b>
    </span>

    <span style="
      border-left:1px solid #475569;
      padding-left:12px;
    ">
      O <b>${d.open.toFixed(2)}</b>
    </span>

    <span>
      H <b style="color:#22c55e">
        ${d.high.toFixed(2)}
      </b>
    </span>

    <span>
      L <b style="color:#ef4444">
        ${d.low.toFixed(2)}
      </b>
    </span>

    <span>
      C <b style="color:${col}">
        ${d.close.toFixed(2)}
      </b>
    </span>

    <span style="color:${col}">
      ${chg >= 0 ? "+" : ""}
      ${chg.toFixed(2)}
      (${pct}%)
    </span>

  </div>
`;
    });

    return () => pc.remove();
  }, [isMaximized]);

  /* push data on change */
  useEffect(() => {
    if (!chart.current || !candleSeries.current || !baseCandles.length) return;

    const step = { "1m": 1, "3m": 3, "5m": 5, "15m": 15 }[tf] || 1;
    const candles = aggregate(baseCandles, step);

    candleSeries.current.setData(candles);
    [overlay, bbUp, bbMid, bbLo].forEach((r) => r.current?.setData([]));
    createSeriesMarkers(candleSeries.current, []);

    // MACD (bottom pane)
    const macd = calcMACD(candles);
    mHist.current.setData(macd.histogram);
    mLine.current.setData(macd.macdLine);
    mSig.current.setData(macd.signalLine);
    createSeriesMarkers(mHist.current, macd.crossovers);

    // top-pane indicator
    let signals = [];
    if (indicator === "vwap") {
      const vwap = calcVWAP(candles, vwapSrc).filter((v) => v.value != null);
      overlay.current.setData(vwap);
      overlay.current.applyOptions({ color: "#f59e0b" });
      signals = calcVWAPSignals(candles, vwap);
    }
    if (indicator === "utbot") {
      const { line, signals: s } = calcUTBot(candles);
      overlay.current.setData(line);
      overlay.current.applyOptions({ color: "#a855f7" });
      signals = s;
    }
    if (indicator === "bb") {
      const bb = calcBB(candles, bbP, bbM);
      bbUp.current.setData(bb.upper);
      bbMid.current.setData(bb.middle);
      bbLo.current.setData(bb.lower);
      signals = calcBBSignals(candles, bb);
    }
    createSeriesMarkers(candleSeries.current, signals);

    chart.current.timeScale().fitContent();
  }, [baseCandles, indicator, tf, vwapSrc, bbP, bbM]);

  /* resize */
  useEffect(() => {
    if (!containerRef.current) return;
    let raf;
    const resize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = containerRef.current?.clientWidth;
        const h = isMaximized ? window.innerHeight - 150 : 500;
        if (w) chart.current?.applyOptions({ width: w, height: h });
      });
    };
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(containerRef.current);
    window.addEventListener("resize", resize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      obs.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [isMaximized]);

  return (
    <>
      <div className="candle-controls">
        <select
          value={indicator}
          onChange={(e) => setIndicator(e.target.value)}
          className="candle-select"
        >
          <option value="vwap">VWAP</option>
          <option value="utbot">UT Bot</option>
          <option value="bb">Bollinger Band</option>
        </select>
        <select
          value={tf}
          onChange={(e) => setTf(e.target.value)}
          className="candle-select"
        >
          {["1m", "3m", "5m", "15m"].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        {indicator === "vwap" && (
          <select
            value={vwapSrc}
            onChange={(e) => setVwapSrc(e.target.value)}
            className="candle-select"
          >
            {["hlc3", "close", "low", "high"].map((v) => (
              <option key={v} value={v}>
                {v.toUpperCase()}
              </option>
            ))}
          </select>
        )}
        {indicator === "bb" && (
          <>
            <select
              value={bbP}
              onChange={(e) => setBbP(+e.target.value)}
              className="candle-select"
            >
              {[10, 20, 50].map((v) => (
                <option key={v} value={v}>
                  BB {v}
                </option>
              ))}
            </select>
            <select
              value={bbM}
              onChange={(e) => setBbM(+e.target.value)}
              className="candle-select"
            >
              {[1.5, 2, 2.5, 3].map((v) => (
                <option key={v} value={v}>
                  {v}σ
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <div
        ref={containerRef}
        className="candle-chart-container"
        style={{
          width: "100%",
          height: isMaximized ? "calc(100vh - 150px)" : "500px",
        }}
      >
        <div ref={priceRef} style={{ width: "100%", height: "100%" }} />
      </div>

      <div className="candle-legend">
        <span className="candle-legend-item">
          <span className="candle-dot" style={{ background: "#f59e0b" }} /> VWAP
        </span>
        <span className="candle-legend-item">
          <span className="candle-dot" style={{ background: "#3b82f6" }} /> MACD
        </span>
        <span className="candle-legend-item">
          <span style={{ color: "#22c55e" }}>▲</span> BUY
        </span>
        <span className="candle-legend-item">
          <span style={{ color: "#ef4444" }}>▼</span> SELL
        </span>
      </div>
    </>
  );
}
